///---------------------------------------------------------------------------
//! Copyright (C) ASQUARED SRL - All Rights Reserved
//* Unauthorized copying of this file, via any medium is strictly prohibited
//* Proprietary and confidential
//* Written by Alexandru Gârbacea <g99.alex@yahoo.com>, September 2022
//? @author g99.alex@yahoo.com
///---------------------------------------------------------------------------

import { getShortMonthName } from './DateService'

const PRICES = [0, 15.99, 19.99, 24.99, 79.99, 'custom']
const SUBTIME = [3, 12, 12, 12, 12, 12]
const NAMES = ['Demo', 'Standard', 'Plus', 'Premium', 'Professional', 'Custom PRO']
const LINES = [1500, 600, 1300, 3000, 10000, 'custom']
const LAYOUTS = [2, 2, 3, 4, 10, 'custom']
const CIF_CHECKS = [true, false, true, true, true, true]
const CONVERT = [true, false, true, true, true, true]
const COMPANIES = [1, 1, 1, 1, 10, 'custom']
const VAL_STAT_CALC = [true, false, true, true, true, true]

const SUB_OPTIONS = [
    {}, // DEMO
    {exchange: false, vat: false, layouts: 2, companies: 1, valStat: false},
    {exchange: true, vat: true, layouts: 3, companies: 1, valStat: true},
    {exchange: true, vat: true, layouts: 4, companies: 1, valStat: true},
    {exchange: true, vat: true, layouts: 10, companies: 10, valStat: true},
    {} // custom
]
const DISPLAY_SUBSCRIPTION_LENGTH = 4

const addMonths = (num, date) => {
    date.setMonth(date.getMonth() + num)
    return date
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Whole months elapsed between two dates.
//?
//? The day of the month decides whether the last month is complete, and that has to hold
//? whatever the calendar months are. The previous version only applied the correction
//? when (endMonth - startMonth) was positive, which is never the case once the period
//? crosses a year end: a subscription started on 20 December read as one month older
//? than it was from 1 January onwards, all the way to its last month.
//-----------------------------------------------------------------------------------------
const getMonthDifference = (startDate, endDate) => {
    let monthDiff =
        (endDate.getFullYear() - startDate.getFullYear()) * 12 +
        (endDate.getMonth() - startDate.getMonth())

    // the month is only complete once the starting day of the month comes round again
    if (endDate.getDate() < startDate.getDate()) monthDiff--

    return monthDiff
}

const dateToString = (date) => {
    return `${date.getDate()}/${date.getMonth()}/${date.getFullYear()}`
}

export const getValStatCalc = (id) => {
    return VAL_STAT_CALC[id]
}

export const getCompanyLimit = (id) => {
    return COMPANIES[id]
}

export const getConvert = (id) => {
    return CONVERT[id]
}

export const getCifCheck = (id) => {
    return CIF_CHECKS[id]
}

export const getLayoutsLimit = (id) => {
    return LAYOUTS[id]
}

//-----------------------------------------------------------------------------------------
//* \brief
//? A numeric allowance (machete, firme, ...) read off a subscription document.
//?
//? What the document says is what was actually sold, so it wins over the price table: an
//? admin can grant a custom number and the app has to honour it - reading the table by plan
//? index silently ignored those grants, while the interface next to the check displayed the
//? granted number, so the user was shown one limit and stopped at another.
//? 'custom' and anything unreadable mean no limit here, which is what Custom PRO is for.
//-----------------------------------------------------------------------------------------
export const subscriptionAllowance = (sub, key, planValue) => {
    const stored = parseInt(sub && sub[key])
    if (!isNaN(stored)) return stored
    const fallback = parseInt(planValue)
    return isNaN(fallback) ? Infinity : fallback
}

export const subDisplayData = () => {
    let subArray = []
    for(let i = 0; i < DISPLAY_SUBSCRIPTION_LENGTH; i++) {
        const index = i + 1
        const objToPush = {
            id: index,
            name: NAMES[index],
            lines: LINES[index] === 9999 ? 'NELIMITAT' : LINES[index],
            time: SUBTIME[index],
            price: PRICES[index],
            options: SUB_OPTIONS[index]
        }
        subArray.push(objToPush)
    }
    return subArray;
}

export const subscriptionPrices = (sub) => {
    return PRICES[+sub]
}

export const subscriptionNames = (sub) => {
    return NAMES[+sub]
}

export const getAllSubscriptionNames = () => {
    return NAMES
}

export const subscriptionLines = (sub) => {
    return LINES[+sub]
}

export const subscriptionTime = (sub) => {
    return SUBTIME[+sub]
}

export const subscriptionExpireDate = (date, months) => {
    months++
    const returnDate = addMonths(months, new Date(date))
    return dateToString(returnDate)
}

export const setStartLines = (sub, months) => {
    const linesNr = subscriptionLines(sub)
    let returnArr = []
    for(let i = 0; i < months; i++) {
        returnArr.push(linesNr)
    }
    return returnArr
}

export const currentSubMonth = (start) => {
    const today = new Date();
    const nrMonths = Math.floor(getMonthDifference(new Date(start), today))
    // a subscription dated in the future is still on its first month
    return nrMonths > 0 ? nrMonths : 0
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Index of the monthly line bucket to read for a subscription.
//?
//? sub.lines holds one entry per month of the subscription (0 .. duration - 1), but
//? isStillActive() deliberately keeps a subscription alive for the whole of its expiry
//? day - and on that day currentSubMonth() equals duration, one past the last bucket.
//? Reading it raw returns undefined, which then turns every line calculation into NaN.
//? Returns -1 when the subscription carries no usable lines array.
//-----------------------------------------------------------------------------------------
export const currentLinesIndex = (sub) => {
    if (!sub || !Array.isArray(sub.lines) || sub.lines.length === 0) return -1
    const last = sub.lines.length - 1
    const current = currentSubMonth(sub.date)
    return current > last ? last : current
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Lines still available this month, or NaN when it cannot be determined.
//-----------------------------------------------------------------------------------------
export const currentLinesAvailable = (sub) => {
    const idx = currentLinesIndex(sub)
    if (idx === -1) return NaN
    return parseInt(sub.lines[idx])
}

//-----------------------------------------------------------------------------------------
//* \brief
//? First day of a subscription month, counted from the day the subscription started and
//? NOT from the 1st of the calendar month - month 1 of a subscription bought on the 15th
//? runs to the 14th of the next calendar month.
//?
//? Derived from the same rule currentSubMonth() applies, month-end included: a calendar
//? month too short to ever reach the start day (February against a 31st) never rolls the
//? counter over, so the next subscription month only opens on the 1st of the month after.
//? Anything built with addMonths() would disagree with the buckets on exactly those dates.
//-----------------------------------------------------------------------------------------
export const subscriptionMonthStart = (sub, monthIndex) => {
    if (!sub || !sub.date) return null
    const start = new Date(sub.date)
    if (isNaN(start.getTime())) return null
    const day = start.getDate()
    const target = new Date(start.getFullYear(), start.getMonth() + monthIndex, 1)
    const daysInTarget = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate()
    if (daysInTarget < day) return new Date(target.getFullYear(), target.getMonth() + 1, 1)
    return new Date(target.getFullYear(), target.getMonth(), day)
}

//-----------------------------------------------------------------------------------------
//* \brief
//? The calendar span a subscription month covers, as dates and as a short label.
//-----------------------------------------------------------------------------------------
export const subscriptionMonthRange = (sub, monthIndex) => {
    const start = subscriptionMonthStart(sub, monthIndex)
    const next = subscriptionMonthStart(sub, monthIndex + 1)
    if (!start || !next) return null
    const end = new Date(next)
    end.setDate(end.getDate() - 1)
    const short = (date) => `${date.getDate()} ${getShortMonthName(date.getMonth() + 1)}`
    return { start, end, shortLabel: short(start), rangeLabel: `${short(start)} - ${short(end)}` }
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Month by month consumption of a subscription, plus the aggregates built on top of it.
//?
//? sub.lines holds the lines LEFT in each month and every bucket is created already full,
//? so the months the subscription has not reached yet still read as untouched. Only the
//? buckets up to the current one carry real data - counting the rest would dilute every
//? average here with months that have not happened.
//? Returns null when the subscription carries nothing usable.
//-----------------------------------------------------------------------------------------
export const subscriptionUsage = (sub) => {
    const idx = currentLinesIndex(sub)
    if (idx === -1) return null
    const allowance = parseInt(sub.linesStart)
    if (isNaN(allowance) || allowance <= 0) return null

    const months = sub.lines.slice(0, idx + 1).map((left, i) => {
        const parsed = parseInt(left)
        //? a bucket can only hold between nothing and a full allowance, whatever it says
        const remaining = isNaN(parsed) ? 0 : Math.min(Math.max(parsed, 0), allowance)
        return {
            month: i + 1,
            used: allowance - remaining,
            remaining,
            //? subscription months are anniversary based, so they have to carry the calendar
            //? span they actually cover - "month 2" on its own invites reading it as February
            range: subscriptionMonthRange(sub, i),
            //? the running month is still being filled, so it is not comparable to the rest
            isCurrent: i === idx
        }
    })

    const completed = months.filter(month => !month.isCurrent)
    const completedUsed = completed.reduce((acc, month) => acc + month.used, 0)

    return {
        allowance,
        months,
        current: months[idx],
        completedMonths: completed.length,
        totalUsed: months.reduce((acc, month) => acc + month.used, 0),
        //? averages leave the running month out - it would always drag them down
        averageUsed: completed.length > 0 ? Math.round(completedUsed / completed.length) : null,
        peakUsed: months.reduce((max, month) => month.used > max ? month.used : max, 0),
        //? a month that ended on nothing is the clearest sign the package is too small
        exhaustedMonths: completed.filter(month => month.remaining === 0).length
    }
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Whole days left until the subscription expires. 0 means it expires today (still usable
//? for the rest of the day, see isStillActive), negative means it already expired.
//-----------------------------------------------------------------------------------------
export const daysUntilExpiry = (date, months) => {
    const expiry = addMonths(parseInt(months), new Date(date))
    expiry.setHours(0, 0, 0, 0)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return Math.round((expiry - today) / 86400000)
}

//-----------------------------------------------------------------------------------------
//* \brief
//? True while the subscription is on its final month, so the user can be told to renew
//? before it lapses. sub.lines holds one entry per month, so the last one is duration - 1.
//-----------------------------------------------------------------------------------------
export const isLastSubMonth = (sub) => {
    if (!sub) return false
    const duration = parseInt(sub.duration)
    if (isNaN(duration) || duration < 1) return false
    return currentSubMonth(sub.date) >= duration - 1
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Whole days until the monthly line allowance is refilled - that is, until the
//? subscription rolls over into its next month, on the same day of the month it started.
//? Nothing in the interface says these lines come back, which is worth telling the user
//? before they conclude they have run out for good.
//? Returns null on the last month, where the subscription expires instead of refilling.
//-----------------------------------------------------------------------------------------
export const daysUntilLinesReset = (sub) => {
    if (!sub || !sub.date || isLastSubMonth(sub)) return null
    const next = addMonths(currentSubMonth(sub.date) + 1, new Date(sub.date))
    next.setHours(0, 0, 0, 0)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const days = Math.round((next - today) / 86400000)
    return days > 0 ? days : 0
}

export const isStillActive = (date, months, disabled) => {
    if(disabled) return false;
    if(currentSubMonth(date) > months) return false;
    const expiryDate = addMonths(months, new Date(date));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return expiryDate > today;
}